/**
 * All external hyperlinks are defined here so that they can be periodically
 * checked to ensure they remain alive (not 404).
 */

const CURL_USER_AGENT = 'curl/8.7.1';

export type Hyperlink = {
    title: string;
    url: string;
    allowedResponseStatuses?: number[];
    forceGetRequest?: boolean;
    forceUserAgent?: string;
};

export const hyperlinks = {
    /*
    General links
     */
    stripe: {
        title: 'Stripe',
        url: 'https://stripe.com',
    },
    campaignMonitor: {
        title: 'Campaign Monitor',
        url: 'https://www.campaignmonitor.com/',
    },
    campaignMonitorPrivacyPolicy: {
        title: 'Campaign Monitor Privacy Policy',
        url: 'https://www.campaignmonitor.com/policies/#privacy-policy',
    },
    googleAnalytics: {
        title: 'Google Analytics',
        url: 'https://marketingplatform.google.com/about/analytics/',
    },
    googlePrivacyPolicy: {
        title: 'Google Privacy Policy',
        url: 'https://policies.google.com/privacy',
    },
    nzPrivacyAct: {
        title: 'New Zealand Privacy Act 2020 - Codes of practice',
        url: 'https://www.privacy.org.nz/privacy-principles/codes-of-practice/',
    },
    treesThatCount: {
        title: 'Trees That Count',
        url: 'https://treesthatcount.co.nz/',
    },
    onePercentCollective: {
        title: 'One Percent Collective',
        url: 'https://www.onepercentcollective.org/',
    },
    onePercentCollectiveCharities: {
        title: 'One Percent Collective - Charities',
        url: 'https://www.onepercentcollective.org/charities',
    },
    typographics: {
        title: 'Typographics',
        url: 'https://typographics.com/',
        // Permanent redirect to https://2025.typographics.com/
        allowedResponseStatuses: [301],
    },
    aTypI: {
        title: 'ATypI',
        url: 'https://atypi.org/',
    },
    typeCon: {
        title: 'TypeCon',
        url: 'https://www.typecon.com/',
    },
    kerning: {
        title: 'Kerning',
        url: 'https://kerning.it/',
    },
    dinz: {
        title: 'DINZ',
        url: 'https://designersinstitute.nz/',
    },
    agi: {
        title: 'Alliance Graphique Internationale',
        url: 'https://a-g-i.org/',
    },
    tdc: {
        title: 'Type Directors Club',
        url: 'https://tdc.org/',
        // Hmmm. This site returns a 403 Forbidden response when being requested from GitHub Actions, only...
        allowedResponseStatuses: [403],
    },
    klimAwardsBestAwardsBlackPin: {
        title: 'John Britten Black Pin',
        url: 'https://bestawards.co.nz/about/#black-pin',
    },
    theArtsFoundation: {
        title: 'The Arts Foundation',
        url: 'https://www.thearts.co.nz/',
        // Hmmm. This site returns a 403 Forbidden response when being requested from GitHub Actions, only...
        allowedResponseStatuses: [403],
    },
    noeBlanco: {
        title: 'Noe Blanco',
        url: 'http://noeblanco.com/',
    },
    fosterType: {
        title: 'Foster Type',
        url: 'https://www.fostertype.com/',
    },
    rosalieWagner: {
        title: 'Rosalie Wagner',
        url: 'https://rosaliewagner.com/',
    },
    thyHa: {
        title: 'Thy Hà',
        url: 'https://thy-ha.com/',
    },
    luuChu: {
        title: 'Lưu Chữ',
        url: 'https://www.luuchu.com/',
    },
    counterForms: {
        title: 'Counter Forms',
        url: 'https://counter-forms.com/',
    },
    benKiel: {
        title: 'Ben Kiel',
        url: 'https://benkiel.com/',
    },
    jasonYuan: {
        title: 'Jason Yuan',
        url: 'https://jasonyuan.design/',
    },
    dia: {
        title: 'DIA',
        url: 'https://www.dia.studio/',
    },
    heraLindsayBird: {
        title: 'Hera Lindsay Bird',
        url: 'https://www.heralindsaybird.com/',
    },
    extendedWhanau: {
        title: 'Extended Whānau',
        url: 'https://extendedwhanau.com/',
    },
    dayMonthYear: {
        title: 'DayMonthYear',
        url: 'https://www.daymonthyear.com/',
    },
    altGroup: {
        title: 'Alt Group',
        url: 'https://altgroup.net/',
    },
    practise: {
        title: 'Practise',
        url: 'https://www.practise.co.uk/',
    },
    springload: {
        title: 'Springload',
        url: 'https://www.springload.co.nz/',
    },
    objectspace: {
        title: 'Objectspace',
        url: 'https://www.objectspace.org.nz/',
    },
    /*
    Social media links
     */
    klimFacebook: {
        title: 'Facebook',
        url: 'https://www.facebook.com/klimtypefoundry/',
    },
    klimTumblr: {
        title: 'Tumblr',
        url: 'https://klim-type-foundry.tumblr.com/',
        allowedResponseStatuses: [403],
    },
    klimInstagram: {
        title: 'Instagram',
        url: 'https://www.instagram.com/klim_type_foundry/',
    },
    klimPinterest: {
        title: 'Pinterest',
        url: 'https://pinterest.com/klimtype/',
        // Permanent redirect to https://nz.pinterest.com/klimtype/
        allowedResponseStatuses: [308],
    },
    klimVimeo: {
        title: 'Vimeo',
        url: 'https://vimeo.com/user20273759',
    },
    klimFlickr: {
        title: 'Flickr',
        url: 'https://www.flickr.com/photos/20994543@N04/',
    },
    klimMastodon: {
        title: 'Mastodon',
        url: 'https://typo.social/@klim',
    },
    /*
    OpenType app support
     */
    openTypeAppSupportAdobeIllustrator: {
        title: 'Adobe Illustrator',
        url: 'https://helpx.adobe.com/illustrator/desktop/design-with-text/special-characters-glyphs/about-character-sets-and-alternate-glyphs.html',
        forceGetRequest: true,
        forceUserAgent: CURL_USER_AGENT,
    },
    openTypeAppSupportAdobeInDesign: {
        title: 'Adobe InDesign',
        url: 'https://helpx.adobe.com/indesign/desktop/fonts/fonts-overview.html',
        forceGetRequest: true,
        forceUserAgent: CURL_USER_AGENT,
    },
    openTypeAppSupportAdobePhotoshop: {
        title: 'Adobe Photoshop',
        url: 'https://helpx.adobe.com/photoshop/desktop/text-typography/characters-glyphs/add-emoji-glyphs.html',
        forceGetRequest: true,
        forceUserAgent: CURL_USER_AGENT,
    },
    openTypeAppSupportAffinityDesigner: {
        title: 'Affinity Designer',
        url: 'https://affinity.help/designer2/en-US.lproj/index.html?page=pages/Text/opentype_fonts.html',
    },
    openTypeAppSupportAffinityPhoto: {
        title: 'Affinity Photo',
        url: 'https://affinity.help/photo2/en-US.lproj/index.html?page=pages/Text/opentype_fonts.html',
    },
    openTypeAppSupportAffinityPublisher: {
        title: 'Affinity Publisher',
        url: 'https://affinity.help/publisher2/en-US.lproj/index.html?page=pages/Text/opentype_fonts.html',
    },
    openTypeAppSupportApplePages: {
        title: 'Apple Pages',
        url: 'https://pimpmytype.com/otf-in-pages/',
    },
    openTypeAppSupportFigma: {
        title: 'Figma',
        url: 'https://help.figma.com/hc/en-us/articles/4913951097367-Use-OpenType-features',
    },
    openTypeAppSupportQuarkXPress: {
        title: 'QuarkXPress',
        url: 'https://www.quark.com/documentation/quarkxpress/2022/english/A%20Guide%20to%20QuarkXPress%202022/_5_3719690306931965E7_6.html#_7_219874868213914E7_6_21',
    },
    openTypeAppSupportSketch: {
        title: 'Sketch',
        url: 'https://www.sketch.com/docs/designing/text/',
    },
    openTypeAppSupportMicrosoftWordAndOutlook: {
        title: 'Microsoft Word & Outlook',
        url: 'https://support.microsoft.com/en-us/word/font-advanced-settings',
    },
    openTypeAppSupportMicrosoftPublisher: {
        title: 'Microsoft Publisher',
        url: 'https://support.microsoft.com/en-us/publisher/use-typographic-styles-to-increase-the-impact-of-your-publication',
    },
    /*
    CSS documentation
     */
    cssDocFontFeatureSettingsProperty: {
        title: 'font-feature-settings CSS property',
        url: 'https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/font-feature-settings',
    },
    cssDocFontFeatureSettingsDescriptor: {
        title: 'font-feature-settings CSS descriptor',
        url: 'https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/At-rules/@font-face/font-feature-settings',
    },
} satisfies Record<string, Hyperlink>;
